import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import "./Analysis.css";

import { auth, db } from "./FirebaseConfig";

import {
    collection,
    doc,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
} from "firebase/firestore";

import { onAuthStateChanged } from "firebase/auth";
import { useTranslation } from "react-i18next";

import {
    Alert,
    Button,
    Card,
    DatePicker,
    Divider,
    Input,
    message,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Tooltip,
} from "antd";

import {
    BarChartOutlined,
    DeleteOutlined,
    DownloadOutlined,
    ExperimentOutlined,
    FileImageOutlined,
    InfoCircleOutlined,
    PlusOutlined,
    ReloadOutlined,
} from "@ant-design/icons";

import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";

import html2canvas from "html2canvas";

const { RangePicker } = DatePicker;

/* =========================================================
   SETTINGS
========================================================= */

const INITIAL_LOAD_LIMIT = 300;

const STORAGE_TOKEN =
    "bb06e309-ca5b-4b6b-9bdd-7ea80efa4ced";

const GROUP_STYLES = [
    {
        color: "#1677ff",
        background: "#eaf3ff",
    },
    {
        color: "#52c41a",
        background: "#f0fae8",
    },
    {
        color: "#fa8c16",
        background: "#fff3e3",
    },
    {
        color: "#722ed1",
        background: "#f4ebff",
    },
    {
        color: "#eb2f96",
        background: "#ffeaf5",
    },
    {
        color: "#13c2c2",
        background: "#e7fafa",
    },
];

const MAX_GROUPS = GROUP_STYLES.length;

/* =========================================================
   MAIN METRICS

   画面上ではこの3つだけを使用
========================================================= */

const METRICS = {
    aperture: {
        labelKey: "analysis.metrics.aperture",
        unit: "µm",

        /*
         * ApertureのみClosed = 0として
         * 分析に含めることができる
         */
        allowClosed: true,
    },

    area: {
        labelKey: "analysis.metrics.area",
        unit: "µm²",

        /*
         * pore segmentation値なのでOpenのみ
         */
        allowClosed: false,
    },

    axisRatio: {
        labelKey: "analysis.metrics.axisRatio",
        unit: "",

        /*
         * Short / Long
         * pore segmentation値なのでOpenのみ
         */
        allowClosed: false,
    },
};

/* =========================================================
   BASIC
========================================================= */

function normalizeEmail(value) {
    return String(value || "")
        .trim()
        .toLowerCase();
}

function toNumber(value) {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return null;
    }

    const n = Number(value);

    return Number.isFinite(n)
        ? n
        : null;
}

function normalizeNumericTimestamp(value) {
    const n = Number(value);

    if (!Number.isFinite(n)) {
        return null;
    }

    const candidates = [
        n,
        n * 10,
        n * 100,
        n * 1000,
    ];

    for (const ms of candidates) {
        const year =
            new Date(ms).getFullYear();

        if (
            year >= 2000 &&
            year <= 2100
        ) {
            return Math.round(ms);
        }
    }

    return null;
}

function toMs(value) {
    if (!value) {
        return null;
    }

    if (
        typeof value?.toDate === "function"
    ) {
        return value.toDate().getTime();
    }

    if (value instanceof Date) {
        return value.getTime();
    }

    if (typeof value === "number") {
        return normalizeNumericTimestamp(value);
    }

    const parsed =
        new Date(value);

    if (!Number.isNaN(parsed.getTime())) {
        return parsed.getTime();
    }

    return null;
}

function normalizePhoto(
    id,
    data = {},
    ownerEmail = ""
) {
    return {
        key: id,

        photopath:
            data.photopath ?? "",

        cultiverName:
            data.cultiverName ?? "",

        date:
            data.date ?? "",

        location:
            data.location ?? "",

        freeText:
            data.freeText ?? "",

        important:
            data.important ?? "",

        temperature:
            data.temperature ??
            data.temperture ??
            "",

        humidity:
            data.humidity ?? "",

        weather:
            data.weather ??
            data.wether ??
            "",

        timestamp:
            data.timestamp ?? "",

        timestampMs:
            toMs(data.timestamp) ??
            toMs(data.date),

        aperture:
            toNumber(data.aperture),

        size:
            toNumber(data.size) ??
            data.size ??
            "",

        /*
         * コピーされたFirestoreデータの場合、
         * 元画像Storageを参照するため重要
         */
        userName:
            data.userName ||
            ownerEmail ||
            "",

        ownerEmail,

        results:
            Array.isArray(data.results)
                ? data.results
                : undefined,
    };
}

function getLabel(item) {
    return String(
        item?.title ??
        item?.label ??
        item?.class ??
        ""
    )
        .trim()
        .toLowerCase();
}

function isClosed(item) {
    const label =
        getLabel(item);

    return (
        label === "closed" ||
        label === "close"
    );
}

function isOpen(item) {
    return (
        getLabel(item) === "open"
    );
}

function getGroupStyle(group) {
    return (
        GROUP_STYLES[
        group?.colorIndex
        ] ||
        GROUP_STYLES[0]
    );
}

/* =========================================================
   STORAGE
========================================================= */

function buildStorageUrl(
    photo,
    fallbackEmail
) {
    /*
     * Storage画面と同じ考え方
     *
     * Firestoreコピー後でも
     * userNameに元Storageのユーザーが残っていれば
     * 元画像を表示できる。
     */
    const storageUser =
        photo?.userName ||
        photo?.ownerEmail ||
        fallbackEmail;

    if (
        !photo?.photopath ||
        !photo?.cultiverName ||
        !storageUser
    ) {
        return null;
    }

    return (
        "https://firebasestorage.googleapis.com/v0/b/stomatascope-fdf90.appspot.com/o/" +
        `${encodeURIComponent(storageUser)}%2F` +
        `${encodeURIComponent(photo.cultiverName)}%2F` +
        `${encodeURIComponent(photo.photopath)}` +
        `?alt=media&token=${STORAGE_TOKEN}`
    );
}

/* =========================================================
   STATISTICS
========================================================= */

function mean(values) {
    if (!values.length) {
        return 0;
    }

    return (
        values.reduce(
            (sum, value) =>
                sum + value,
            0
        ) /
        values.length
    );
}

function median(values) {
    if (!values.length) {
        return 0;
    }

    const sorted =
        [...values].sort(
            (a, b) => a - b
        );

    const middle =
        Math.floor(
            sorted.length / 2
        );

    if (
        sorted.length % 2 === 0
    ) {
        return (
            sorted[middle - 1] +
            sorted[middle]
        ) / 2;
    }

    return sorted[middle];
}

function quantile(
    values,
    q
) {
    if (!values.length) {
        return 0;
    }

    const sorted =
        [...values].sort(
            (a, b) => a - b
        );

    const position =
        (sorted.length - 1) * q;

    const base =
        Math.floor(position);

    const rest =
        position - base;

    if (
        sorted[base + 1] !==
        undefined
    ) {
        return (
            sorted[base] +
            rest *
            (
                sorted[base + 1] -
                sorted[base]
            )
        );
    }

    return sorted[base];
}

function standardDeviation(values) {
    if (
        values.length < 2
    ) {
        return 0;
    }

    const avg =
        mean(values);

    const variance =
        values.reduce(
            (sum, value) =>
                sum +
                Math.pow(
                    value - avg,
                    2
                ),
            0
        ) /
        (values.length - 1);

    return Math.sqrt(
        variance
    );
}

/* =========================================================
   NORMAL CDF
========================================================= */

function erf(x) {
    const sign =
        x >= 0 ? 1 : -1;

    x =
        Math.abs(x);

    const a1 =
        0.254829592;

    const a2 =
        -0.284496736;

    const a3 =
        1.421413741;

    const a4 =
        -1.453152027;

    const a5 =
        1.061405429;

    const p =
        0.3275911;

    const t =
        1 /
        (1 + p * x);

    const y =
        1 -
        (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) *
            t *
            Math.exp(
                -x * x
            ));

    return sign * y;
}

function normalCdf(x) {
    return (
        0.5 *
        (
            1 +
            erf(
                x /
                Math.sqrt(2)
            )
        )
    );
}

/* =========================================================
   GAMMA / CHI-SQUARE
========================================================= */

function logGamma(z) {
    const coefficients = [
        676.5203681218851,
        -1259.1392167224028,
        771.3234287776531,
        -176.6150291621406,
        12.507343278686905,
        -0.13857109526572012,
        9.984369578019572e-6,
        1.5056327351493116e-7,
    ];

    if (z < 0.5) {
        return (
            Math.log(Math.PI) -
            Math.log(
                Math.sin(
                    Math.PI * z
                )
            ) -
            logGamma(
                1 - z
            )
        );
    }

    z -= 1;

    let x =
        0.9999999999998099;

    for (
        let i = 0;
        i <
        coefficients.length;
        i++
    ) {
        x +=
            coefficients[i] /
            (z + i + 1);
    }

    const t =
        z +
        coefficients.length -
        0.5;

    return (
        0.5 *
        Math.log(
            2 * Math.PI
        ) +
        (z + 0.5) *
        Math.log(t) -
        t +
        Math.log(x)
    );
}

function regularizedGammaP(
    a,
    x
) {
    if (
        x < 0 ||
        a <= 0
    ) {
        return NaN;
    }

    if (x === 0) {
        return 0;
    }

    if (
        x <
        a + 1
    ) {
        let ap = a;

        let sum =
            1 / a;

        let del =
            sum;

        for (
            let n = 1;
            n <= 200;
            n++
        ) {
            ap += 1;

            del *=
                x / ap;

            sum +=
                del;

            if (
                Math.abs(del) <
                Math.abs(sum) *
                1e-12
            ) {
                break;
            }
        }

        return (
            sum *
            Math.exp(
                -x +
                a *
                Math.log(x) -
                logGamma(a)
            )
        );
    }

    let b =
        x + 1 - a;

    let c =
        1 / 1e-30;

    let d =
        1 / b;

    let h =
        d;

    for (
        let i = 1;
        i <= 200;
        i++
    ) {
        const an =
            -i *
            (i - a);

        b += 2;

        d =
            an * d +
            b;

        if (
            Math.abs(d) <
            1e-30
        ) {
            d =
                1e-30;
        }

        c =
            b +
            an / c;

        if (
            Math.abs(c) <
            1e-30
        ) {
            c =
                1e-30;
        }

        d =
            1 / d;

        const del =
            d * c;

        h *= del;

        if (
            Math.abs(
                del - 1
            ) <
            1e-12
        ) {
            break;
        }
    }

    const q =
        Math.exp(
            -x +
            a *
            Math.log(x) -
            logGamma(a)
        ) *
        h;

    return 1 - q;
}

function chiSquareSurvival(
    x,
    df
) {
    if (
        x < 0 ||
        df <= 0
    ) {
        return 1;
    }

    const cdf =
        regularizedGammaP(
            df / 2,
            x / 2
        );

    return Math.max(
        0,
        Math.min(
            1,
            1 - cdf
        )
    );
}

/* =========================================================
   MANN-WHITNEY U
========================================================= */

function mannWhitneyUTest(
    groupA,
    groupB
) {
    const a =
        groupA.filter(
            Number.isFinite
        );

    const b =
        groupB.filter(
            Number.isFinite
        );

    if (
        a.length < 2 ||
        b.length < 2
    ) {
        return null;
    }

    const combined = [
        ...a.map(
            (value) => ({
                value,
                group: "A",
            })
        ),

        ...b.map(
            (value) => ({
                value,
                group: "B",
            })
        ),
    ].sort(
        (x, y) =>
            x.value -
            y.value
    );

    let i = 0;

    const ranked = [];

    const tieSizes =
        [];

    while (
        i <
        combined.length
    ) {
        let j = i;

        while (
            j + 1 <
            combined.length &&
            combined[
                j + 1
            ].value ===
            combined[i].value
        ) {
            j++;
        }

        const averageRank =
            (
                i +
                1 +
                j +
                1
            ) / 2;

        const tieSize =
            j - i + 1;

        if (
            tieSize > 1
        ) {
            tieSizes.push(
                tieSize
            );
        }

        for (
            let k = i;
            k <= j;
            k++
        ) {
            ranked.push({
                ...combined[k],

                rank:
                    averageRank,
            });
        }

        i =
            j + 1;
    }

    const rankSumA =
        ranked
            .filter(
                (item) =>
                    item.group ===
                    "A"
            )
            .reduce(
                (
                    sum,
                    item
                ) =>
                    sum +
                    item.rank,
                0
            );

    const n1 =
        a.length;

    const n2 =
        b.length;

    const n =
        n1 + n2;

    const u1 =
        rankSumA -
        (
            n1 *
            (n1 + 1)
        ) /
        2;

    const u2 =
        n1 *
        n2 -
        u1;

    const u =
        Math.min(
            u1,
            u2
        );

    const meanU =
        (
            n1 *
            n2
        ) / 2;

    const tieCorrection =
        tieSizes.reduce(
            (
                sum,
                tie
            ) =>
                sum +
                (
                    Math.pow(
                        tie,
                        3
                    ) -
                    tie
                ),
            0
        );

    let variance =
        (
            n1 *
            n2 /
            12
        ) *
        (
            n +
            1 -
            tieCorrection /
            (
                n *
                (n - 1)
            )
        );

    if (
        !Number.isFinite(
            variance
        ) ||
        variance <= 0
    ) {
        variance =
            (
                n1 *
                n2 *
                (n + 1)
            ) /
            12;
    }

    const sd =
        Math.sqrt(
            variance
        );

    const diff =
        Math.abs(
            u -
            meanU
        );

    const corrected =
        Math.max(
            0,
            diff - 0.5
        );

    const z =
        sd > 0
            ? corrected / sd
            : 0;

    const p =
        2 *
        (
            1 -
            normalCdf(z)
        );

    return {
        test:
            "Mann–Whitney U",

        statistic:
            u,

        p:
            Math.min(
                1,
                Math.max(
                    0,
                    p
                )
            ),
    };
}

/* =========================================================
   KRUSKAL-WALLIS
========================================================= */

function kruskalWallisTest(
    groups
) {
    const validGroups =
        groups
            .map(
                (group) => ({
                    ...group,

                    values:
                        group.values.filter(
                            Number.isFinite
                        ),
                })
            )
            .filter(
                (group) =>
                    group.values.length >
                    0
            );

    if (
        validGroups.length <
        3
    ) {
        return null;
    }

    const combined =
        [];

    validGroups.forEach(
        (
            group,
            groupIndex
        ) => {
            group.values.forEach(
                (value) => {
                    combined.push({
                        value,
                        groupIndex,
                    });
                }
            );
        }
    );

    combined.sort(
        (a, b) =>
            a.value -
            b.value
    );

    const ranks =
        new Array(
            combined.length
        );

    const tieSizes =
        [];

    let i = 0;

    while (
        i <
        combined.length
    ) {
        let j = i;

        while (
            j + 1 <
            combined.length &&
            combined[
                j + 1
            ].value ===
            combined[i].value
        ) {
            j++;
        }

        const rank =
            (
                i +
                1 +
                j +
                1
            ) / 2;

        const tieSize =
            j - i + 1;

        if (
            tieSize > 1
        ) {
            tieSizes.push(
                tieSize
            );
        }

        for (
            let k = i;
            k <= j;
            k++
        ) {
            ranks[k] =
                rank;
        }

        i =
            j + 1;
    }

    const rankSums =
        validGroups.map(
            () => 0
        );

    combined.forEach(
        (
            item,
            index
        ) => {
            rankSums[
                item.groupIndex
            ] +=
                ranks[index];
        }
    );

    const N =
        combined.length;

    let sumTerm =
        0;

    validGroups.forEach(
        (
            group,
            index
        ) => {
            sumTerm +=
                Math.pow(
                    rankSums[index],
                    2
                ) /
                group.values.length;
        }
    );

    let H =
        (
            12 /
            (
                N *
                (N + 1)
            )
        ) *
        sumTerm -
        3 *
        (N + 1);

    const tieTerm =
        tieSizes.reduce(
            (
                sum,
                tie
            ) =>
                sum +
                (
                    Math.pow(
                        tie,
                        3
                    ) -
                    tie
                ),
            0
        );

    const denominator =
        Math.pow(
            N,
            3
        ) -
        N;

    if (
        denominator > 0
    ) {
        const tieCorrection =
            1 -
            tieTerm /
            denominator;

        if (
            tieCorrection > 0
        ) {
            H /=
                tieCorrection;
        }
    }

    const df =
        validGroups.length -
        1;

    const p =
        chiSquareSurvival(
            H,
            df
        );

    return {
        test:
            "Kruskal–Wallis",

        statistic:
            H,

        df,

        p,
    };
}

/* =========================================================
   HISTOGRAM
========================================================= */

function createHistogram(
    groupRows,
    binCount = 12
) {
    const allValues =
        groupRows.flatMap(
            (group) =>
                group.values
        );

    if (
        !allValues.length
    ) {
        return [];
    }

    const min =
        Math.min(
            ...allValues
        );

    const max =
        Math.max(
            ...allValues
        );

    if (
        min === max
    ) {
        const row = {
            start: min,
            end: max,
            isLast: true,

            range:
                min.toFixed(
                    max <= 1
                        ? 2
                        : 1
                ),
        };

        groupRows.forEach(
            (group) => {
                row[group.id] =
                    group.values.length;
            }
        );

        return [row];
    }

    const width =
        (
            max -
            min
        ) /
        binCount;

    const decimals =
        max <= 1
            ? 2
            : 1;

    const bins =
        Array.from(
            {
                length:
                    binCount,
            },
            (
                _,
                index
            ) => {
                const start =
                    min +
                    index *
                    width;

                const end =
                    start +
                    width;

                const row = {
                    start,
                    end,

                    isLast:
                        index ===
                        binCount - 1,

                    range:
                        `${start.toFixed(
                            decimals
                        )}–${end.toFixed(
                            decimals
                        )}`,
                };

                groupRows.forEach(
                    (group) => {
                        row[
                            group.id
                        ] = 0;
                    }
                );

                return row;
            }
        );

    groupRows.forEach(
        (group) => {
            group.values.forEach(
                (value) => {
                    let index =
                        Math.floor(
                            (
                                value -
                                min
                            ) /
                            width
                        );

                    if (
                        index >=
                        binCount
                    ) {
                        index =
                            binCount - 1;
                    }

                    if (
                        index < 0
                    ) {
                        index = 0;
                    }

                    bins[index][
                        group.id
                    ] += 1;
                }
            );
        }
    );

    return bins;
}

/* =========================================================
   BOX PLOT
========================================================= */

/* =========================================================
   BOX PLOT + INDIVIDUAL MEASUREMENTS
========================================================= */

function MultiBoxPlot({
    groups,
    metricUnit,
}) {
    const valid =
        groups.filter(
            (group) =>
                group.values.length >
                0
        );

    const all =
        valid.flatMap(
            (group) =>
                group.values
        );

    if (!all.length) {
        return (
            <div className="analysis-empty">
                -
            </div>
        );
    }

    const width =
        Math.max(
            900,
            valid.length * 190
        );

    const height =
        420;

    const paddingTop =
        40;

    const paddingBottom =
        75;

    const paddingLeft =
        75;

    const paddingRight =
        35;

    /*
     * Y軸範囲
     */
    let min =
        Math.min(...all);

    let max =
        Math.max(...all);

    /*
     * ApertureでClosed=0がある場合も
     * 0が見やすいようにする
     */
    if (min > 0) {
        min = 0;
    }

    const rawRange =
        max - min || 1;

    /*
     * 上側に少し余白
     */
    max =
        max +
        rawRange * 0.08;

    const range =
        max - min || 1;

    const y = (
        value
    ) => {
        const ratio =
            (
                value -
                min
            ) /
            range;

        return (
            height -
            paddingBottom -
            ratio *
            (
                height -
                paddingTop -
                paddingBottom
            )
        );
    };

    const usableWidth =
        width -
        paddingLeft -
        paddingRight;

    const groupWidth =
        usableWidth /
        valid.length;

    /*
     * 再描画しても点が動かない
     * deterministic jitter
     */
    const getJitter =
        (
            index,
            seed = 0
        ) => {
            const value =
                (
                    (
                        index +
                        1 +
                        seed * 17
                    ) *
                    37
                ) %
                101;

            return (
                value / 100 -
                0.5
            );
        };

    /*
     * Y軸tick
     */
    const tickCount = 5;

    const ticks =
        Array.from(
            {
                length:
                    tickCount + 1,
            },
            (
                _,
                index
            ) => {
                const value =
                    min +
                    (
                        range /
                        tickCount
                    ) *
                    index;

                return value;
            }
        );

    return (
        <div className="analysis-boxplot-wrap">
            <svg
                viewBox={`0 0 ${width} ${height}`}
                className="analysis-boxplot"
            >
                {/* =========================================
            GRID + Y AXIS
        ========================================= */}

                {ticks.map(
                    (
                        tick,
                        index
                    ) => {
                        const tickY =
                            y(tick);

                        return (
                            <g
                                key={`tick-${index}`}
                            >
                                <line
                                    x1={
                                        paddingLeft
                                    }
                                    y1={
                                        tickY
                                    }
                                    x2={
                                        width -
                                        paddingRight
                                    }
                                    y2={
                                        tickY
                                    }
                                    stroke="#e5e7eb"
                                    strokeDasharray="4 4"
                                />

                                <text
                                    x={
                                        paddingLeft -
                                        12
                                    }
                                    y={
                                        tickY +
                                        4
                                    }
                                    textAnchor="end"
                                    fontSize="11"
                                    fill="#667085"
                                >
                                    {tick.toFixed(
                                        max <= 1
                                            ? 2
                                            : 1
                                    )}
                                </text>
                            </g>
                        );
                    }
                )}

                <line
                    x1={
                        paddingLeft
                    }
                    y1={
                        paddingTop
                    }
                    x2={
                        paddingLeft
                    }
                    y2={
                        height -
                        paddingBottom
                    }
                    stroke="#cbd5e1"
                />

                <line
                    x1={
                        paddingLeft
                    }
                    y1={
                        height -
                        paddingBottom
                    }
                    x2={
                        width -
                        paddingRight
                    }
                    y2={
                        height -
                        paddingBottom
                    }
                    stroke="#cbd5e1"
                />

                {metricUnit && (
                    <text
                        x={18}
                        y={
                            height / 2
                        }
                        transform={`rotate(-90 18 ${height / 2})`}
                        textAnchor="middle"
                        className="boxplot-unit-label"
                    >
                        {metricUnit}
                    </text>
                )}

                {/* =========================================
            GROUPS
        ========================================= */}

                {valid.map(
                    (
                        group,
                        groupIndex
                    ) => {
                        const values =
                            group.values;

                        const q1 =
                            quantile(
                                values,
                                0.25
                            );

                        const med =
                            median(
                                values
                            );

                        const q3 =
                            quantile(
                                values,
                                0.75
                            );

                        const iqr =
                            q3 - q1;

                        /*
                         * 論文で一般的な
                         * 1.5×IQR whisker
                         */
                        const lowerFence =
                            q1 -
                            1.5 *
                            iqr;

                        const upperFence =
                            q3 +
                            1.5 *
                            iqr;

                        const sorted =
                            [...values].sort(
                                (a, b) =>
                                    a - b
                            );

                        /*
                         * Fence内の実測最小・最大
                         */
                        const lowerWhisker =
                            sorted.find(
                                (value) =>
                                    value >=
                                    lowerFence
                            ) ??
                            sorted[0];

                        const upperWhisker =
                            [...sorted]
                                .reverse()
                                .find(
                                    (value) =>
                                        value <=
                                        upperFence
                                ) ??
                            sorted[
                            sorted.length -
                            1
                            ];

                        const x =
                            paddingLeft +
                            groupWidth *
                            groupIndex +
                            groupWidth /
                            2;

                        const boxWidth =
                            Math.min(
                                90,
                                groupWidth *
                                0.48
                            );

                        /*
                         * 個別点の横方向範囲
                         *
                         * 箱より少し広く散らす
                         */
                        const pointSpread =
                            Math.min(
                                130,
                                groupWidth *
                                0.7
                            );

                        return (
                            <g
                                key={
                                    group.id
                                }
                            >
                                {/* =================================
                    INDIVIDUAL MEASUREMENTS
                    箱より先に描く
                ================================= */}

                                {values.map(
                                    (
                                        value,
                                        pointIndex
                                    ) => {
                                        const jitter =
                                            getJitter(
                                                pointIndex,
                                                groupIndex
                                            );

                                        const pointX =
                                            x +
                                            jitter *
                                            pointSpread;

                                        /*
                                         * 外れ値だけ少し強めに
                                         */
                                        const isOutlier =
                                            value <
                                            lowerFence ||
                                            value >
                                            upperFence;

                                        return (
                                            <circle
                                                key={`${group.id}-point-${pointIndex}`}
                                                cx={
                                                    pointX
                                                }
                                                cy={
                                                    y(
                                                        value
                                                    )
                                                }
                                                r={
                                                    isOutlier
                                                        ? 2.6
                                                        : 2.1
                                                }
                                                fill={
                                                    group.color
                                                }
                                                fillOpacity={
                                                    isOutlier
                                                        ? 0.58
                                                        : 0.30
                                                }
                                                stroke={
                                                    isOutlier
                                                        ? group.color
                                                        : "none"
                                                }
                                                strokeWidth={
                                                    isOutlier
                                                        ? 0.6
                                                        : 0
                                                }
                                            />
                                        );
                                    }
                                )}

                                {/* =================================
                    WHISKER
                ================================= */}

                                <line
                                    x1={x}
                                    x2={x}
                                    y1={
                                        y(
                                            upperWhisker
                                        )
                                    }
                                    y2={
                                        y(
                                            lowerWhisker
                                        )
                                    }
                                    stroke="#64748b"
                                    strokeWidth="2"
                                />

                                <line
                                    x1={
                                        x - 24
                                    }
                                    x2={
                                        x + 24
                                    }
                                    y1={
                                        y(
                                            upperWhisker
                                        )
                                    }
                                    y2={
                                        y(
                                            upperWhisker
                                        )
                                    }
                                    stroke="#64748b"
                                    strokeWidth="2"
                                />

                                <line
                                    x1={
                                        x - 24
                                    }
                                    x2={
                                        x + 24
                                    }
                                    y1={
                                        y(
                                            lowerWhisker
                                        )
                                    }
                                    y2={
                                        y(
                                            lowerWhisker
                                        )
                                    }
                                    stroke="#64748b"
                                    strokeWidth="2"
                                />

                                {/* =================================
                    BOX
                ================================= */}

                                <rect
                                    x={
                                        x -
                                        boxWidth /
                                        2
                                    }
                                    y={
                                        y(q3)
                                    }
                                    width={
                                        boxWidth
                                    }
                                    height={
                                        Math.max(
                                            2,
                                            y(q1) -
                                            y(q3)
                                        )
                                    }
                                    rx="8"
                                    fill={
                                        group.background
                                    }
                                    fillOpacity="0.88"
                                    stroke={
                                        group.color
                                    }
                                    strokeWidth="2"
                                />

                                {/* =================================
                    MEDIAN
                ================================= */}

                                <line
                                    x1={
                                        x -
                                        boxWidth /
                                        2
                                    }
                                    x2={
                                        x +
                                        boxWidth /
                                        2
                                    }
                                    y1={
                                        y(med)
                                    }
                                    y2={
                                        y(med)
                                    }
                                    stroke="#273444"
                                    strokeWidth="4"
                                />

                                {/* =================================
                    MEDIAN LABEL
                ================================= */}

                                <text
                                    x={x}
                                    y={
                                        y(q3) -
                                        13
                                    }
                                    textAnchor="middle"
                                    className="boxplot-value"
                                >
                                    Median{" "}
                                    {med.toFixed(
                                        max <= 1
                                            ? 3
                                            : 2
                                    )}
                                </text>

                                {/* =================================
                    GROUP LABEL
                ================================= */}

                                <text
                                    x={x}
                                    y={
                                        height -
                                        35
                                    }
                                    textAnchor="middle"
                                    className="boxplot-group-label"
                                >
                                    {
                                        group.name
                                    }
                                </text>

                                {/* =================================
                    n
                ================================= */}

                                <text
                                    x={x}
                                    y={
                                        height -
                                        17
                                    }
                                    textAnchor="middle"
                                    fontSize="10"
                                    fill="#98a2b3"
                                >
                                    n ={" "}
                                    {
                                        values.length
                                    }
                                </text>
                            </g>
                        );
                    }
                )}
            </svg>
        </div>
    );
}

/* =========================================================
   GROUP FACTORY
========================================================= */

function createInitialGroups() {
    return [
        {
            id:
                "group1",

            name:
                "Control",

            photoKeys:
                [],

            colorIndex:
                0,
        },

        {
            id:
                "group2",

            name:
                "Treatment",

            photoKeys:
                [],

            colorIndex:
                1,
        },
    ];
}

/* =========================================================
   MAIN
========================================================= */

function Analysis() {
    const { t } =
        useTranslation();

    const reportRef =
        useRef(null);

    const fullDocCache =
        useRef({});

    const [user, setUser] =
        useState(null);

    const [
        authReady,
        setAuthReady,
    ] =
        useState(false);

    const [
        isAdmin,
        setIsAdmin,
    ] =
        useState(false);

    const [
        photos,
        setPhotos,
    ] =
        useState([]);

    const [
        loading,
        setLoading,
    ] =
        useState(true);

    const [
        loadingAll,
        setLoadingAll,
    ] =
        useState(false);

    const [
        loadedAll,
        setLoadedAll,
    ] =
        useState(false);

    const [
        targetEmailInput,
        setTargetEmailInput,
    ] =
        useState("");

    const [
        activeTargetEmail,
        setActiveTargetEmail,
    ] =
        useState("");

    const [
        dateRange,
        setDateRange,
    ] =
        useState(null);

    const [
        selectedCultivar,
        setSelectedCultivar,
    ] =
        useState("all");

    const [
        selectedLocation,
        setSelectedLocation,
    ] =
        useState("all");

    const [
        commentSearch,
        setCommentSearch,
    ] =
        useState("");

    const [
        tableSelectedKeys,
        setTableSelectedKeys,
    ] =
        useState([]);

    const [
        groups,
        setGroups,
    ] =
        useState(
            createInitialGroups
        );

    const [
        selectedTargetGroup,
        setSelectedTargetGroup,
    ] =
        useState(
            "group1"
        );

    const [
        metric,
        setMetric,
    ] =
        useState(
            "aperture"
        );

    const [
        includeClose,
        setIncludeClose,
    ] =
        useState(true);

    const [
        analysisLoading,
        setAnalysisLoading,
    ] =
        useState(false);

    const [
        analysisDocsByGroup,
        setAnalysisDocsByGroup,
    ] =
        useState({});

    const [
        selectedHistogramBin,
        setSelectedHistogramBin,
    ] =
        useState(null);

    const [
        histogramStomas,
        setHistogramStomas,
    ] =
        useState([]);

    const [
        histogramPreviewLoading,
        setHistogramPreviewLoading,
    ] =
        useState(false);

    /* ======================================================
       CURRENT METRIC
    ====================================================== */

    const metricInfo =
        METRICS[
        metric
        ];

    const metricLabel =
        t(
            metricInfo.labelKey
        );

    const metricDisplay =
        metricInfo.unit
            ? `${metricLabel} (${metricInfo.unit})`
            : metricLabel;

    /*
     * Area / Aspect Ratioは
     * 常にOpen stomata only
     */
    const effectiveIncludeClose =
        metricInfo.allowClosed
            ? includeClose
            : false;

    /* ======================================================
       AUTH
    ====================================================== */

    useEffect(() => {
        const unsubscribe =
            onAuthStateChanged(
                auth,

                async (
                    currentUser
                ) => {
                    setUser(
                        currentUser
                    );

                    if (
                        !currentUser
                    ) {
                        setAuthReady(
                            true
                        );

                        setLoading(
                            false
                        );

                        return;
                    }

                    const ownEmail =
                        normalizeEmail(
                            currentUser.email
                        );

                    setTargetEmailInput(
                        ownEmail
                    );

                    setActiveTargetEmail(
                        ownEmail
                    );

                    try {
                        const userRef =
                            doc(
                                db,
                                "users",
                                currentUser.uid
                            );

                        const snap =
                            await getDoc(
                                userRef
                            );

                        setIsAdmin(
                            snap.exists() &&
                            snap.data()
                                ?.role ===
                            "admin"
                        );
                    } catch (
                    error
                    ) {
                        console.warn(
                            "Admin check failed",
                            error
                        );

                        setIsAdmin(
                            false
                        );
                    }

                    setAuthReady(
                        true
                    );
                }
            );

        return () =>
            unsubscribe();
    }, []);

    /* ======================================================
       RESET
    ====================================================== */

    const resetAnalysis =
        useCallback(
            () => {
                setGroups(
                    createInitialGroups()
                );

                setSelectedTargetGroup(
                    "group1"
                );

                setTableSelectedKeys(
                    []
                );

                setAnalysisDocsByGroup(
                    {}
                );

                setSelectedHistogramBin(
                    null
                );

                setHistogramStomas(
                    []
                );
            },
            []
        );

    /* ======================================================
       LOAD
    ====================================================== */

    const loadPhotos =
        useCallback(
            async (
                email,
                all = false
            ) => {
                const normalizedEmail =
                    normalizeEmail(
                        email
                    );

                if (
                    !normalizedEmail
                ) {
                    return;
                }

                if (all) {
                    setLoadingAll(
                        true
                    );
                } else {
                    setLoading(
                        true
                    );
                }

                try {
                    let snapshot;

                    if (all) {
                        snapshot =
                            await getDocs(
                                collection(
                                    db,
                                    normalizedEmail
                                )
                            );
                    } else {
                        try {
                            const q =
                                query(
                                    collection(
                                        db,
                                        normalizedEmail
                                    ),

                                    orderBy(
                                        "timestamp",
                                        "desc"
                                    ),

                                    limit(
                                        INITIAL_LOAD_LIMIT
                                    )
                                );

                            snapshot =
                                await getDocs(
                                    q
                                );
                        } catch (
                        error
                        ) {
                            console.warn(
                                "timestamp orderBy fallback",
                                error
                            );

                            snapshot =
                                await getDocs(
                                    collection(
                                        db,
                                        normalizedEmail
                                    )
                                );
                        }
                    }

                    const next =
                        snapshot.docs
                            .filter(
                                (item) =>
                                    item.id !==
                                    "_settings"
                            )
                            .map(
                                (item) =>
                                    normalizePhoto(
                                        item.id,
                                        item.data(),
                                        normalizedEmail
                                    )
                            )
                            .sort(
                                (a, b) =>
                                    (
                                        b.timestampMs ||
                                        0
                                    ) -
                                    (
                                        a.timestampMs ||
                                        0
                                    )
                            );

                    setPhotos(
                        next
                    );

                    setLoadedAll(
                        all
                    );

                    fullDocCache.current =
                        {};

                    resetAnalysis();
                } catch (
                error
                ) {
                    console.error(
                        error
                    );

                    message.error(
                        t(
                            "analysis.messages.loadFailed"
                        )
                    );
                } finally {
                    setLoading(
                        false
                    );

                    setLoadingAll(
                        false
                    );
                }
            },
            [
                resetAnalysis,
                t,
            ]
        );

    useEffect(() => {
        if (
            !authReady ||
            !activeTargetEmail
        ) {
            return;
        }

        loadPhotos(
            activeTargetEmail,
            false
        );
    }, [
        authReady,
        activeTargetEmail,
        loadPhotos,
    ]);

    /* ======================================================
       ADMIN
    ====================================================== */

    const handleTargetSearch =
        () => {
            const email =
                normalizeEmail(
                    targetEmailInput
                );

            if (!email) {
                message.warning(
                    t(
                        "analysis.messages.enterEmail"
                    )
                );

                return;
            }

            if (
                !isAdmin &&
                email !==
                normalizeEmail(
                    user?.email
                )
            ) {
                message.error(
                    t(
                        "analysis.messages.adminOnly"
                    )
                );

                return;
            }

            if (
                email ===
                activeTargetEmail
            ) {
                loadPhotos(
                    email,
                    false
                );
            } else {
                setActiveTargetEmail(
                    email
                );
            }
        };

    /* ======================================================
       FILTER
    ====================================================== */

    const cultivarOptions =
        useMemo(
            () =>
                Array.from(
                    new Set(
                        photos
                            .map(
                                (item) =>
                                    item.cultiverName
                            )
                            .filter(Boolean)
                    )
                )
                    .sort()
                    .map(
                        (value) => ({
                            label:
                                value,

                            value,
                        })
                    ),
            [photos]
        );

    const locationOptions =
        useMemo(
            () =>
                Array.from(
                    new Set(
                        photos
                            .map(
                                (item) =>
                                    item.location
                            )
                            .filter(Boolean)
                    )
                )
                    .sort()
                    .map(
                        (value) => ({
                            label:
                                value,

                            value,
                        })
                    ),
            [photos]
        );

    const filteredPhotos =
        useMemo(
            () => {
                let data =
                    [...photos];

                if (
                    selectedCultivar !==
                    "all"
                ) {
                    data =
                        data.filter(
                            (item) =>
                                item.cultiverName ===
                                selectedCultivar
                        );
                }

                if (
                    selectedLocation !==
                    "all"
                ) {
                    data =
                        data.filter(
                            (item) =>
                                item.location ===
                                selectedLocation
                        );
                }

                if (
                    commentSearch.trim()
                ) {
                    const keyword =
                        commentSearch
                            .trim()
                            .toLowerCase();

                    data =
                        data.filter(
                            (item) =>
                                String(
                                    item.freeText ||
                                    ""
                                )
                                    .toLowerCase()
                                    .includes(
                                        keyword
                                    )
                        );
                }

                if (
                    dateRange &&
                    dateRange[0] &&
                    dateRange[1]
                ) {
                    const startDate =
                        new Date(
                            dateRange[
                                0
                            ].valueOf()
                        );

                    startDate.setHours(
                        0,
                        0,
                        0,
                        0
                    );

                    const endDate =
                        new Date(
                            dateRange[
                                1
                            ].valueOf()
                        );

                    endDate.setHours(
                        23,
                        59,
                        59,
                        999
                    );

                    data =
                        data.filter(
                            (item) => {
                                if (
                                    !item.timestampMs
                                ) {
                                    return false;
                                }

                                return (
                                    item.timestampMs >=
                                    startDate.getTime() &&
                                    item.timestampMs <=
                                    endDate.getTime()
                                );
                            }
                        );
                }

                return data;
            },
            [
                photos,
                selectedCultivar,
                selectedLocation,
                commentSearch,
                dateRange,
            ]
        );

    /* ======================================================
       GROUPS
    ====================================================== */

    const addGroup =
        () => {
            if (
                groups.length >=
                MAX_GROUPS
            ) {
                message.warning(
                    t(
                        "analysis.messages.maxGroups",
                        {
                            max:
                                MAX_GROUPS,
                        }
                    )
                );

                return;
            }

            const usedColorIndexes =
                new Set(
                    groups.map(
                        (group) =>
                            group.colorIndex
                    )
                );

            const nextColorIndex =
                GROUP_STYLES.findIndex(
                    (
                        _,
                        index
                    ) =>
                        !usedColorIndexes.has(
                            index
                        )
                );

            if (
                nextColorIndex ===
                -1
            ) {
                return;
            }

            const id =
                `group_${Date.now()}`;

            const newGroup = {
                id,

                name:
                    `${t(
                        "analysis.groups.group"
                    )} ${groups.length + 1}`,

                photoKeys:
                    [],

                colorIndex:
                    nextColorIndex,
            };

            setGroups(
                (prev) => [
                    ...prev,
                    newGroup,
                ]
            );

            setSelectedTargetGroup(
                id
            );
        };

    const updateGroupName =
        (
            groupId,
            name
        ) => {
            setGroups(
                (prev) =>
                    prev.map(
                        (group) =>
                            group.id ===
                                groupId
                                ? {
                                    ...group,
                                    name,
                                }
                                : group
                    )
            );
        };

    const clearGroup =
        (groupId) => {
            setGroups(
                (prev) =>
                    prev.map(
                        (group) =>
                            group.id ===
                                groupId
                                ? {
                                    ...group,
                                    photoKeys:
                                        [],
                                }
                                : group
                    )
            );

            setAnalysisDocsByGroup(
                (prev) => {
                    const next =
                    {
                        ...prev,
                    };

                    delete next[
                        groupId
                    ];

                    return next;
                }
            );
        };

    const deleteGroup =
        (groupId) => {
            if (
                groups.length <=
                2
            ) {
                message.warning(
                    t(
                        "analysis.messages.minimumGroups"
                    )
                );

                return;
            }

            const next =
                groups.filter(
                    (group) =>
                        group.id !==
                        groupId
                );

            setGroups(
                next
            );

            if (
                selectedTargetGroup ===
                groupId
            ) {
                setSelectedTargetGroup(
                    next[0].id
                );
            }

            setAnalysisDocsByGroup(
                (prev) => {
                    const result =
                    {
                        ...prev,
                    };

                    delete result[
                        groupId
                    ];

                    return result;
                }
            );
        };

    const addSelectedToGroup =
        () => {
            if (
                !tableSelectedKeys.length
            ) {
                message.warning(
                    t(
                        "analysis.messages.selectImages"
                    )
                );

                return;
            }

            if (
                !selectedTargetGroup
            ) {
                return;
            }

            setGroups(
                (prev) =>
                    prev.map(
                        (group) => {
                            if (
                                group.id ===
                                selectedTargetGroup
                            ) {
                                return {
                                    ...group,

                                    photoKeys:
                                        Array.from(
                                            new Set([
                                                ...group.photoKeys,
                                                ...tableSelectedKeys,
                                            ])
                                        ),
                                };
                            }

                            return {
                                ...group,

                                photoKeys:
                                    group.photoKeys.filter(
                                        (key) =>
                                            !tableSelectedKeys.includes(
                                                key
                                            )
                                    ),
                            };
                        }
                    )
            );

            setTableSelectedKeys(
                []
            );
        };

    const assignedGroupByPhotoKey =
        useMemo(
            () => {
                const map =
                    new Map();

                groups.forEach(
                    (group) => {
                        group.photoKeys.forEach(
                            (key) => {
                                map.set(
                                    key,
                                    group
                                );
                            }
                        );
                    }
                );

                return map;
            },
            [groups]
        );

    /* ======================================================
       FULL DOC
    ====================================================== */

    const loadFullPhoto =
        useCallback(
            async (
                photo
            ) => {
                if (
                    fullDocCache.current[
                    photo.key
                    ]
                ) {
                    return fullDocCache.current[
                        photo.key
                    ];
                }

                const snap =
                    await getDoc(
                        doc(
                            db,
                            activeTargetEmail,
                            photo.key
                        )
                    );

                if (
                    !snap.exists()
                ) {
                    return {
                        ...photo,

                        results:
                            [],
                    };
                }

                const full =
                    normalizePhoto(
                        snap.id,
                        snap.data(),
                        activeTargetEmail
                    );

                fullDocCache.current[
                    photo.key
                ] =
                    full;

                return full;
            },
            [
                activeTargetEmail,
            ]
        );

    /* ======================================================
       EXECUTE
    ====================================================== */

    const executeAnalysis =
        async () => {
            const activeGroups =
                groups.filter(
                    (group) =>
                        group.photoKeys.length >
                        0
                );

            if (
                activeGroups.length <
                2
            ) {
                message.warning(
                    t(
                        "analysis.messages.needTwoGroups"
                    )
                );

                return;
            }

            setAnalysisLoading(
                true
            );

            setSelectedHistogramBin(
                null
            );

            setHistogramStomas(
                []
            );

            try {
                const nextDocs =
                    {};

                for (
                    const group of
                    activeGroups
                ) {
                    const groupPhotos =
                        photos.filter(
                            (photo) =>
                                group.photoKeys.includes(
                                    photo.key
                                )
                        );

                    nextDocs[
                        group.id
                    ] =
                        await Promise.all(
                            groupPhotos.map(
                                loadFullPhoto
                            )
                        );
                }

                setAnalysisDocsByGroup(
                    nextDocs
                );

                message.success(
                    t(
                        "analysis.messages.analysisLoaded"
                    )
                );
            } catch (
            error
            ) {
                console.error(
                    error
                );

                message.error(
                    t(
                        "analysis.messages.analysisFailed"
                    )
                );
            } finally {
                setAnalysisLoading(
                    false
                );
            }
        };

    /* ======================================================
       DETECTION STATS
  
       Open率は解析指標から完全に独立。
  
       今回の学会表示では
       Openなのにapertureが0/nullの異常データは除外。
    ====================================================== */

    const calculateDetectionStats =
        useCallback(
            (docs) => {
                let openCount =
                    0;

                let closedCount =
                    0;

                docs.forEach(
                    (photo) => {
                        const results =
                            Array.isArray(
                                photo.results
                            )
                                ? photo.results
                                : [];

                        results.forEach(
                            (result) => {
                                const open =
                                    isOpen(
                                        result
                                    );

                                const closed =
                                    isClosed(
                                        result
                                    );

                                /*
                                 * 学会表示用：
                                 * Openなのにaperture算出不能のものは
                                 * 集計から除外
                                 */
                                if (open) {
                                    const aperture =
                                        toNumber(
                                            result.aperture
                                        );

                                    if (
                                        aperture ===
                                        null ||
                                        aperture <=
                                        0
                                    ) {
                                        return;
                                    }

                                    openCount +=
                                        1;

                                    return;
                                }

                                if (closed) {
                                    closedCount +=
                                        1;
                                }
                            }
                        );
                    }
                );

                const detected =
                    openCount +
                    closedCount;

                return {
                    openCount,

                    closedCount,

                    detected,

                    openRate:
                        detected > 0
                            ? (
                                openCount /
                                detected
                            ) *
                            100
                            : 0,
                };
            },
            []
        );

    /* ======================================================
       ANALYSIS ROWS
  
       Aperture:
         Open   → aperture
         Closed → 0 µm（includeClose時）
  
       Area:
         Open only
  
       Aspect Ratio:
         Open only
         Short / Long
    ====================================================== */

    const makeStomataRows =
        useCallback(
            (
                docs,
                group
            ) => {
                const rows =
                    [];

                docs.forEach(
                    (photo) => {
                        const results =
                            Array.isArray(
                                photo.results
                            )
                                ? photo.results
                                : [];

                        results.forEach(
                            (
                                result,
                                index
                            ) => {
                                const open =
                                    isOpen(
                                        result
                                    );

                                const closed =
                                    isClosed(
                                        result
                                    );

                                const rawAperture =
                                    toNumber(
                                        result.aperture
                                    );

                                const poreArea =
                                    toNumber(
                                        result.area
                                    );

                                const axisLong =
                                    toNumber(
                                        result.axisLong
                                    );

                                const axisShort =
                                    toNumber(
                                        result.axisShort
                                    );

                                const axisRatio =
                                    axisLong !==
                                        null &&
                                        axisLong > 0 &&
                                        axisShort !==
                                        null &&
                                        axisShort >= 0
                                        ? axisShort /
                                        axisLong
                                        : null;

                                /*
                                 * Openなのにapertureが0/nullの
                                 * AI算出異常値は今回除外
                                 */
                                if (
                                    open &&
                                    (
                                        rawAperture ===
                                        null ||
                                        rawAperture <=
                                        0
                                    )
                                ) {
                                    return;
                                }

                                /*
                                 * Aperture
                                 */
                                let value =
                                    null;

                                if (
                                    metric ===
                                    "aperture"
                                ) {
                                    if (open) {
                                        value =
                                            rawAperture;
                                    }

                                    if (
                                        closed &&
                                        effectiveIncludeClose
                                    ) {
                                        value =
                                            0;
                                    }

                                    if (
                                        closed &&
                                        !effectiveIncludeClose
                                    ) {
                                        return;
                                    }
                                }

                                /*
                                 * Area
                                 *
                                 * Openのみ
                                 */
                                if (
                                    metric ===
                                    "area"
                                ) {
                                    if (!open) {
                                        return;
                                    }

                                    value =
                                        poreArea;
                                }

                                /*
                                 * Aspect Ratio
                                 *
                                 * Openのみ
                                 */
                                if (
                                    metric ===
                                    "axisRatio"
                                ) {
                                    if (!open) {
                                        return;
                                    }

                                    value =
                                        axisRatio;
                                }

                                if (
                                    value ===
                                    null ||
                                    !Number.isFinite(
                                        value
                                    )
                                ) {
                                    return;
                                }

                                const style =
                                    getGroupStyle(
                                        group
                                    );

                                rows.push({
                                    groupId:
                                        group.id,

                                    groupName:
                                        group.name,

                                    groupColor:
                                        style.color,

                                    photoKey:
                                        photo.key,

                                    imageName:
                                        photo.photopath,

                                    cultivar:
                                        photo.cultiverName,

                                    date:
                                        photo.date,

                                    comment:
                                        photo.freeText,

                                    temperature:
                                        photo.temperature,

                                    humidity:
                                        photo.humidity,

                                    stomaIndex:
                                        index,

                                    id:
                                        result.id ??
                                        index,

                                    label:
                                        getLabel(
                                            result
                                        ),

                                    open,

                                    closed,

                                    value,

                                    aperture:
                                        rawAperture,

                                    area:
                                        poreArea,

                                    axisLong,

                                    axisShort,

                                    axisRatio,

                                    confidence:
                                        toNumber(
                                            result.confidence
                                        ),
                                });
                            }
                        );
                    }
                );

                return rows;
            },
            [
                metric,
                effectiveIncludeClose,
            ]
        );

    /* ======================================================
       GROUP RESULTS
    ====================================================== */

    const analysisGroups =
        useMemo(
            () =>
                groups
                    .map(
                        (group) => {
                            const docs =
                                analysisDocsByGroup[
                                group.id
                                ] ||
                                [];

                            const rows =
                                makeStomataRows(
                                    docs,
                                    group
                                );

                            const values =
                                rows.map(
                                    (item) =>
                                        item.value
                                );

                            const style =
                                getGroupStyle(
                                    group
                                );

                            const detectionStats =
                                calculateDetectionStats(
                                    docs
                                );

                            return {
                                ...group,

                                color:
                                    style.color,

                                background:
                                    style.background,

                                docs,

                                rows,

                                values,

                                detectionStats,
                            };
                        }
                    )
                    .filter(
                        (group) =>
                            group.docs.length >
                            0 ||
                            group.photoKeys.length >
                            0
                    ),
            [
                groups,
                analysisDocsByGroup,
                makeStomataRows,
                calculateDetectionStats,
            ]
        );

    const analyzedGroups =
        useMemo(
            () =>
                analysisGroups.filter(
                    (group) =>
                        group.values.length >
                        0
                ),
            [
                analysisGroups,
            ]
        );

    /* ======================================================
       SUMMARY
  
       Detected / Open Rateはmetric非依存
       Analyzed nはmetric依存
    ====================================================== */

    const summaries =
        useMemo(
            () => {
                const map =
                    {};

                analyzedGroups.forEach(
                    (group) => {
                        const values =
                            group.values;

                        const detection =
                            group.detectionStats;

                        map[
                            group.id
                        ] = {
                            detected:
                                detection.detected,

                            openCount:
                                detection.openCount,

                            closedCount:
                                detection.closedCount,

                            openRate:
                                detection.openRate,

                            analyzedN:
                                values.length,

                            median:
                                median(
                                    values
                                ),

                            mean:
                                mean(
                                    values
                                ),

                            sd:
                                standardDeviation(
                                    values
                                ),

                            q1:
                                quantile(
                                    values,
                                    0.25
                                ),

                            q3:
                                quantile(
                                    values,
                                    0.75
                                ),
                        };
                    }
                );

                return map;
            },
            [
                analyzedGroups,
            ]
        );

    /* ======================================================
       STAT TEST
    ====================================================== */

    const statisticalResult =
        useMemo(
            () => {
                if (
                    analyzedGroups.length ===
                    2
                ) {
                    return mannWhitneyUTest(
                        analyzedGroups[
                            0
                        ].values,

                        analyzedGroups[
                            1
                        ].values
                    );
                }

                if (
                    analyzedGroups.length >=
                    3
                ) {
                    return kruskalWallisTest(
                        analyzedGroups
                    );
                }

                return null;
            },
            [
                analyzedGroups,
            ]
        );

    /* ======================================================
       HISTOGRAM
    ====================================================== */

    const histogram =
        useMemo(
            () =>
                createHistogram(
                    analyzedGroups
                ),
            [
                analyzedGroups,
            ]
        );

    /* ======================================================
       HISTOGRAM CLICK
    ====================================================== */

    const handleHistogramClick =
        async (
            barData,
            groupId
        ) => {
            const bin =
                barData?.payload ??
                barData;

            if (!bin) {
                return;
            }

            const group =
                analyzedGroups.find(
                    (item) =>
                        item.id ===
                        groupId
                );

            if (!group) {
                return;
            }

            const matched =
                group.rows.filter(
                    (item) => {
                        if (
                            bin.isLast
                        ) {
                            return (
                                item.value >=
                                bin.start &&
                                item.value <=
                                bin.end
                            );
                        }

                        return (
                            item.value >=
                            bin.start &&
                            item.value <
                            bin.end
                        );
                    }
                );

            setSelectedHistogramBin({
                ...bin,

                groupId,

                groupName:
                    group.name,

                color:
                    group.color,
            });

            setHistogramStomas(
                []
            );

            setHistogramPreviewLoading(
                true
            );

            try {
                const grouped =
                    new Map();

                matched.forEach(
                    (item) => {
                        if (
                            !grouped.has(
                                item.photoKey
                            )
                        ) {
                            grouped.set(
                                item.photoKey,
                                []
                            );
                        }

                        grouped
                            .get(
                                item.photoKey
                            )
                            .push(
                                item
                            );
                    }
                );

                const thumbnails =
                    [];

                for (
                    const [
                        photoKey,
                        rows,
                    ] of grouped.entries()
                ) {
                    const photo =
                        group.docs.find(
                            (item) =>
                                item.key ===
                                photoKey
                        );

                    if (!photo) {
                        continue;
                    }

                    const imageUrl =
                        buildStorageUrl(
                            photo,
                            activeTargetEmail
                        );

                    if (!imageUrl) {
                        continue;
                    }

                    try {
                        const response =
                            await fetch(
                                imageUrl
                            );

                        if (
                            !response.ok
                        ) {
                            console.warn(
                                "Image fetch failed:",
                                photo.photopath
                            );

                            continue;
                        }

                        const blob =
                            await response.blob();

                        let imageBitmap;

                        try {
                            imageBitmap =
                                await createImageBitmap(
                                    blob
                                );
                        } catch (
                        error
                        ) {
                            console.warn(
                                "Image decode failed",
                                error
                            );

                            continue;
                        }

                        const results =
                            Array.isArray(
                                photo.results
                            )
                                ? photo.results
                                : [];

                        for (
                            const row of
                            rows
                        ) {
                            const result =
                                results[
                                row.stomaIndex
                                ];

                            if (!result) {
                                continue;
                            }

                            const loc =
                                result.location ||
                                {};

                            const left =
                                Number(
                                    loc.left
                                );

                            const top =
                                Number(
                                    loc.top
                                );

                            const right =
                                Number(
                                    loc.right
                                );

                            const bottom =
                                Number(
                                    loc.bottom
                                );

                            if (
                                !Number.isFinite(
                                    left
                                ) ||
                                !Number.isFinite(
                                    top
                                ) ||
                                !Number.isFinite(
                                    right
                                ) ||
                                !Number.isFinite(
                                    bottom
                                )
                            ) {
                                continue;
                            }

                            const boxWidth =
                                right -
                                left;

                            const boxHeight =
                                bottom -
                                top;

                            if (
                                boxWidth <= 0 ||
                                boxHeight <= 0
                            ) {
                                continue;
                            }

                            const padding =
                                30;

                            const cropLeft =
                                Math.max(
                                    0,
                                    left -
                                    padding
                                );

                            const cropTop =
                                Math.max(
                                    0,
                                    top -
                                    padding
                                );

                            const cropRight =
                                Math.min(
                                    imageBitmap.width,
                                    right +
                                    padding
                                );

                            const cropBottom =
                                Math.min(
                                    imageBitmap.height,
                                    bottom +
                                    padding
                                );

                            const cropWidth =
                                cropRight -
                                cropLeft;

                            const cropHeight =
                                cropBottom -
                                cropTop;

                            const canvas =
                                document.createElement(
                                    "canvas"
                                );

                            canvas.width =
                                cropWidth;

                            canvas.height =
                                cropHeight;

                            const ctx =
                                canvas.getContext(
                                    "2d"
                                );

                            ctx.drawImage(
                                imageBitmap,

                                cropLeft,
                                cropTop,
                                cropWidth,
                                cropHeight,

                                0,
                                0,
                                cropWidth,
                                cropHeight
                            );

                            ctx.strokeStyle =
                                row.open
                                    ? "#ff4d4f"
                                    : "#1677ff";

                            ctx.lineWidth =
                                3;

                            ctx.strokeRect(
                                left -
                                cropLeft,

                                top -
                                cropTop,

                                boxWidth,
                                boxHeight
                            );

                            thumbnails.push({
                                ...row,

                                image:
                                    canvas.toDataURL(
                                        "image/jpeg",
                                        0.9
                                    ),

                                sourceImage:
                                    photo.photopath,

                                temperature:
                                    photo.temperature,

                                humidity:
                                    photo.humidity,

                                comment:
                                    photo.freeText,
                            });
                        }

                        if (
                            typeof imageBitmap.close ===
                            "function"
                        ) {
                            imageBitmap.close();
                        }
                    } catch (
                    error
                    ) {
                        console.warn(
                            "Stoma crop failed",
                            error
                        );
                    }
                }

                thumbnails.sort(
                    (a, b) =>
                        a.value -
                        b.value
                );

                setHistogramStomas(
                    thumbnails
                );
            } catch (
            error
            ) {
                console.error(
                    error
                );

                message.error(
                    t(
                        "analysis.messages.previewFailed"
                    )
                );
            } finally {
                setHistogramPreviewLoading(
                    false
                );
            }
        };

    /* ======================================================
       CSV
    ====================================================== */

    const exportCsv =
        () => {
            const all =
                analyzedGroups.flatMap(
                    (group) =>
                        group.rows
                );

            if (!all.length) {
                message.warning(
                    t(
                        "analysis.messages.noResults"
                    )
                );

                return;
            }

            const headers = [
                "Group",
                "ImageName",
                "Cultivar",
                "Date",
                "Comment",
                "StomaIndex",
                "StomaId",
                "Label",
                "Metric",
                "Value",
                "Unit",
                "Aperture",
                "PoreArea",
                "AxisLong",
                "AxisShort",
                "PoreAspectRatio",
                "Confidence",
            ];

            const escapeCsv =
                (value) => {
                    if (
                        value === null ||
                        value === undefined
                    ) {
                        return "";
                    }

                    const text =
                        String(value);

                    if (
                        /[",\n\r]/.test(
                            text
                        )
                    ) {
                        return `"${text.replace(
                            /"/g,
                            '""'
                        )}"`;
                    }

                    return text;
                };

            const rows =
                all.map(
                    (item) => [
                        item.groupName,
                        item.imageName,
                        item.cultivar,
                        item.date,
                        item.comment,
                        item.stomaIndex,
                        item.id,
                        item.label,
                        metric,
                        item.value,
                        metricInfo.unit,
                        item.aperture,
                        item.area,
                        item.axisLong,
                        item.axisShort,
                        item.axisRatio,
                        item.confidence,
                    ]
                );

            const csv =
                "\uFEFF" +
                [
                    headers,
                    ...rows,
                ]
                    .map(
                        (row) =>
                            row
                                .map(
                                    escapeCsv
                                )
                                .join(",")
                    )
                    .join("\n");

            const blob =
                new Blob(
                    [csv],
                    {
                        type:
                            "text/csv;charset=utf-8",
                    }
                );

            const url =
                URL.createObjectURL(
                    blob
                );

            const a =
                document.createElement(
                    "a"
                );

            a.href =
                url;

            a.download =
                `StomataScope_analysis_${metric}_${Date.now()}.csv`;

            document.body.appendChild(
                a
            );

            a.click();

            document.body.removeChild(
                a
            );

            URL.revokeObjectURL(
                url
            );
        };

    /* ======================================================
       PNG
    ====================================================== */

    const exportPng =
        async () => {
            if (
                !reportRef.current
            ) {
                return;
            }

            try {
                const canvas =
                    await html2canvas(
                        reportRef.current,
                        {
                            scale: 2,

                            backgroundColor:
                                "#ffffff",

                            useCORS:
                                true,
                        }
                    );

                const link =
                    document.createElement(
                        "a"
                    );

                link.download =
                    `StomataScope_analysis_${Date.now()}.png`;

                link.href =
                    canvas.toDataURL(
                        "image/png"
                    );

                link.click();
            } catch (
            error
            ) {
                console.error(
                    error
                );

                message.error(
                    t(
                        "analysis.messages.pngFailed"
                    )
                );
            }
        };

    /* ======================================================
       TABLE
    ====================================================== */

    const columns =
        useMemo(
            () => [
                {
                    title:
                        t(
                            "analysis.table.date"
                        ),

                    key:
                        "date",

                    width:
                        170,

                    render:
                        (
                            _,
                            item
                        ) =>
                            item.date ||
                            "-",
                },

                {
                    title:
                        t(
                            "analysis.table.cultivar"
                        ),

                    dataIndex:
                        "cultiverName",

                    key:
                        "cultiverName",

                    width:
                        150,
                },

                {
                    title:
                        t(
                            "analysis.table.location"
                        ),

                    dataIndex:
                        "location",

                    key:
                        "location",

                    width:
                        130,
                },

                {
                    title:
                        t(
                            "analysis.table.image"
                        ),

                    dataIndex:
                        "photopath",

                    key:
                        "photopath",

                    width:
                        260,

                    render:
                        (value) => (
                            <Space
                                size={6}
                            >
                                <FileImageOutlined />

                                <span>
                                    {value ||
                                        "-"}
                                </span>
                            </Space>
                        ),
                },

                {
                    title:
                        t(
                            "analysis.table.comment"
                        ),

                    dataIndex:
                        "freeText",

                    key:
                        "freeText",

                    width:
                        260,

                    render:
                        (value) => (
                            <div
                                style={{
                                    maxWidth:
                                        260,

                                    whiteSpace:
                                        "nowrap",

                                    overflow:
                                        "hidden",

                                    textOverflow:
                                        "ellipsis",
                                }}
                                title={
                                    value ||
                                    ""
                                }
                            >
                                {value ||
                                    "-"}
                            </div>
                        ),
                },

                {
                    title:
                        t(
                            "analysis.table.count"
                        ),

                    dataIndex:
                        "size",

                    key:
                        "size",

                    width:
                        90,
                },

                {
                    title:
                        t(
                            "analysis.table.group"
                        ),

                    key:
                        "group",

                    width:
                        130,

                    render:
                        (
                            _,
                            item
                        ) => {
                            const group =
                                assignedGroupByPhotoKey.get(
                                    item.key
                                );

                            if (!group) {
                                return "-";
                            }

                            const style =
                                getGroupStyle(
                                    group
                                );

                            return (
                                <Tag
                                    color={
                                        style.color
                                    }
                                >
                                    {
                                        group.name
                                    }
                                </Tag>
                            );
                        },
                },
            ],
            [
                t,
                assignedGroupByPhotoKey,
            ]
        );

    /* ======================================================
       RESULTS
    ====================================================== */

    const hasResult =
        analyzedGroups.length >
        0 &&
        analyzedGroups.some(
            (group) =>
                group.values.length >
                0
        );

    if (
        !authReady ||
        loading
    ) {
        return (
            <div className="analysis-loading">
                <Spin
                    size="large"
                />

                <div>
                    {t(
                        "analysis.loading"
                    )}
                </div>
            </div>
        );
    }

    /* ======================================================
       UI
    ====================================================== */

    return (
        <div className="analysis-page">

            {/* ===============================================
          HEADER
      =============================================== */}

            <div className="analysis-header">
                <div>
                    <div className="analysis-title-row">
                        <BarChartOutlined />

                        <h1>
                            {t(
                                "analysis.title"
                            )}
                        </h1>
                    </div>

                    <p>
                        {t(
                            "analysis.subtitle"
                        )}
                    </p>
                </div>

                <Space wrap>
                    <Button
                        icon={
                            <DownloadOutlined />
                        }
                        onClick={
                            exportCsv
                        }
                        disabled={
                            !hasResult
                        }
                    >
                        {t(
                            "analysis.csv"
                        )}
                    </Button>

                    <Button
                        type="primary"
                        onClick={
                            exportPng
                        }
                        disabled={
                            !hasResult
                        }
                    >
                        {t(
                            "analysis.exportImage"
                        )}
                    </Button>
                </Space>
            </div>

            {/* ===============================================
          ADMIN
      =============================================== */}

            {isAdmin && (
                <Card
                    size="small"
                    className="analysis-admin-card"
                >
                    <Space wrap>
                        <Input.Search
                            style={{
                                width:
                                    360,
                            }}
                            placeholder={
                                t(
                                    "analysis.admin.emailPlaceholder"
                                )
                            }
                            value={
                                targetEmailInput
                            }
                            onChange={(e) =>
                                setTargetEmailInput(
                                    e.target.value
                                )
                            }
                            onSearch={
                                handleTargetSearch
                            }
                            enterButton={
                                t(
                                    "analysis.admin.load"
                                )
                            }
                        />

                        <span>
                            {t(
                                "analysis.admin.target"
                            )}
                            ：
                            <strong>
                                {
                                    activeTargetEmail
                                }
                            </strong>
                        </span>
                    </Space>
                </Card>
            )}

            {/* ===============================================
          1 FILTER
      =============================================== */}

            <Card
                className="analysis-filter-card"
                title={
                    t(
                        "analysis.filter.title"
                    )
                }
            >
                <Space
                    wrap
                    size="middle"
                >
                    <div className="analysis-filter-item">
                        <label>
                            {t(
                                "analysis.filter.date"
                            )}
                        </label>

                        <RangePicker
                            value={
                                dateRange
                            }
                            onChange={
                                setDateRange
                            }
                            allowClear
                        />
                    </div>

                    <div className="analysis-filter-item">
                        <label>
                            {t(
                                "analysis.filter.cultivar"
                            )}
                        </label>

                        <Select
                            value={
                                selectedCultivar
                            }
                            style={{
                                width:
                                    180,
                            }}
                            onChange={
                                setSelectedCultivar
                            }
                            options={[
                                {
                                    label:
                                        t(
                                            "analysis.filter.all"
                                        ),

                                    value:
                                        "all",
                                },

                                ...cultivarOptions,
                            ]}
                        />
                    </div>

                    <div className="analysis-filter-item">
                        <label>
                            {t(
                                "analysis.filter.location"
                            )}
                        </label>

                        <Select
                            value={
                                selectedLocation
                            }
                            style={{
                                width:
                                    180,
                            }}
                            onChange={
                                setSelectedLocation
                            }
                            options={[
                                {
                                    label:
                                        t(
                                            "analysis.filter.all"
                                        ),

                                    value:
                                        "all",
                                },

                                ...locationOptions,
                            ]}
                        />
                    </div>

                    <div className="analysis-filter-item">
                        <label>
                            {t(
                                "analysis.filter.comment"
                            )}
                        </label>

                        <Input
                            value={
                                commentSearch
                            }
                            onChange={(e) =>
                                setCommentSearch(
                                    e.target.value
                                )
                            }
                            allowClear
                            placeholder={
                                t(
                                    "analysis.filter.commentPlaceholder"
                                )
                            }
                            style={{
                                width:
                                    220,
                            }}
                        />
                    </div>

                    <div className="analysis-filter-item">
                        <label>
                            {t(
                                "analysis.filter.load"
                            )}
                        </label>

                        <Button
                            icon={
                                <ReloadOutlined />
                            }
                            loading={
                                loadingAll
                            }
                            disabled={
                                loadedAll ||
                                loadingAll
                            }
                            onClick={() =>
                                loadPhotos(
                                    activeTargetEmail,
                                    true
                                )
                            }
                        >
                            {t(
                                "analysis.filter.loadAll"
                            )}
                        </Button>
                    </div>

                    <span className="analysis-data-count">
                        {t(
                            "analysis.filter.displayed"
                        )}
                        ：
                        {
                            filteredPhotos.length
                        }

                        {" / "}

                        {t(
                            "analysis.filter.loaded"
                        )}
                        ：
                        {
                            photos.length
                        }

                        {loadedAll
                            ? ` (${t(
                                "analysis.filter.allLoaded"
                            )})`
                            : ""}
                    </span>
                </Space>
            </Card>

            {/* ===============================================
          2 GROUPS
      =============================================== */}

            <Card
                className="analysis-group-card"
                title={
                    t(
                        "analysis.groups.title"
                    )
                }
                extra={
                    <Button
                        icon={
                            <PlusOutlined />
                        }
                        onClick={
                            addGroup
                        }
                        disabled={
                            groups.length >=
                            MAX_GROUPS
                        }
                    >
                        {t(
                            "analysis.groups.addGroup"
                        )}
                    </Button>
                }
            >
                <div className="analysis-groups-grid">
                    {groups.map(
                        (group) => {
                            const style =
                                getGroupStyle(
                                    group
                                );

                            const isSelected =
                                selectedTargetGroup ===
                                group.id;

                            return (
                                <div
                                    key={
                                        group.id
                                    }
                                    className={`analysis-dynamic-group ${isSelected
                                        ? "analysis-dynamic-group-selected"
                                        : ""
                                        }`}
                                    style={{
                                        borderColor:
                                            style.color,

                                        background:
                                            style.background,

                                        boxShadow:
                                            isSelected
                                                ? `0 0 0 2px ${style.color}22`
                                                : "none",
                                    }}
                                    onClick={() =>
                                        setSelectedTargetGroup(
                                            group.id
                                        )
                                    }
                                >
                                    <div className="analysis-group-color-line">
                                        <span
                                            className="analysis-group-color-dot"
                                            style={{
                                                background:
                                                    style.color,
                                            }}
                                        />

                                        <span>
                                            {isSelected
                                                ? t(
                                                    "analysis.groups.target"
                                                )
                                                : t(
                                                    "analysis.groups.group"
                                                )}
                                        </span>
                                    </div>

                                    <div className="analysis-dynamic-group-top">
                                        <Input
                                            value={
                                                group.name
                                            }
                                            onClick={(e) =>
                                                e.stopPropagation()
                                            }
                                            onChange={(e) =>
                                                updateGroupName(
                                                    group.id,
                                                    e.target.value
                                                )
                                            }
                                            className="analysis-group-name-input"
                                        />

                                        <strong>
                                            {
                                                group.photoKeys.length
                                            }
                                        </strong>

                                        <span>
                                            {t(
                                                "analysis.groups.images"
                                            )}
                                        </span>
                                    </div>

                                    <div className="analysis-dynamic-group-actions">
                                        <Button
                                            size="small"
                                            type={
                                                isSelected
                                                    ? "primary"
                                                    : "default"
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                setSelectedTargetGroup(
                                                    group.id
                                                );
                                            }}
                                        >
                                            {isSelected
                                                ? t(
                                                    "analysis.groups.currentTarget"
                                                )
                                                : t(
                                                    "analysis.groups.setTarget"
                                                )}
                                        </Button>

                                        <Button
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                clearGroup(
                                                    group.id
                                                );
                                            }}
                                        >
                                            {t(
                                                "analysis.groups.clear"
                                            )}
                                        </Button>

                                        {groups.length >
                                            2 && (
                                                <Button
                                                    size="small"
                                                    danger
                                                    type="text"
                                                    icon={
                                                        <DeleteOutlined />
                                                    }
                                                    onClick={(e) => {
                                                        e.stopPropagation();

                                                        deleteGroup(
                                                            group.id
                                                        );
                                                    }}
                                                >
                                                    {t(
                                                        "analysis.groups.delete"
                                                    )}
                                                </Button>
                                            )}
                                    </div>
                                </div>
                            );
                        }
                    )}
                </div>

                <Divider />

                <Space
                    wrap
                    style={{
                        marginBottom:
                            12,
                    }}
                >
                    <Select
                        value={
                            selectedTargetGroup
                        }
                        style={{
                            width:
                                240,
                        }}
                        onChange={
                            setSelectedTargetGroup
                        }
                        options={
                            groups.map(
                                (group) => {
                                    const style =
                                        getGroupStyle(
                                            group
                                        );

                                    return {
                                        value:
                                            group.id,

                                        label: (
                                            <Space
                                                size={6}
                                            >
                                                <span
                                                    style={{
                                                        width:
                                                            9,

                                                        height:
                                                            9,

                                                        borderRadius:
                                                            "50%",

                                                        display:
                                                            "inline-block",

                                                        background:
                                                            style.color,
                                                    }}
                                                />

                                                <span>
                                                    {t(
                                                        "analysis.groups.target"
                                                    )}
                                                    ：
                                                    {
                                                        group.name
                                                    }
                                                </span>
                                            </Space>
                                        ),
                                    };
                                }
                            )
                        }
                    />

                    <Button
                        type="primary"
                        onClick={
                            addSelectedToGroup
                        }
                    >
                        {t(
                            "analysis.groups.addSelected"
                        )}
                    </Button>

                    <Button
                        onClick={() =>
                            setTableSelectedKeys(
                                []
                            )
                        }
                    >
                        {t(
                            "analysis.groups.clearSelection"
                        )}
                    </Button>

                    <span className="analysis-selection-count">
                        {t(
                            "analysis.groups.selected"
                        )}
                        ：
                        {
                            tableSelectedKeys.length
                        }
                    </span>
                </Space>

                <Table
                    rowKey="key"
                    size="small"
                    columns={
                        columns
                    }
                    dataSource={
                        filteredPhotos
                    }
                    rowSelection={{
                        selectedRowKeys:
                            tableSelectedKeys,

                        onChange:
                            setTableSelectedKeys,

                        preserveSelectedRowKeys:
                            true,
                    }}
                    pagination={{
                        defaultPageSize:
                            20,

                        showSizeChanger:
                            true,

                        pageSizeOptions: [
                            "20",
                            "50",
                            "100",
                            "200",
                        ],
                    }}
                    scroll={{
                        x:
                            1200,

                        y:
                            420,
                    }}
                />
            </Card>

            {/* ===============================================
          3 CONDITIONS
      =============================================== */}

            <Card
                className="analysis-condition-card"
                title={
                    t(
                        "analysis.conditions.title"
                    )
                }
            >
                <Space
                    wrap
                    size="large"
                    align="end"
                >
                    <div className="analysis-filter-item">
                        <label>
                            {t(
                                "analysis.conditions.metric"
                            )}
                        </label>

                        <Select
                            value={
                                metric
                            }
                            style={{
                                width:
                                    310,
                            }}
                            onChange={(value) => {
                                setMetric(
                                    value
                                );

                                setSelectedHistogramBin(
                                    null
                                );

                                setHistogramStomas(
                                    []
                                );
                            }}
                            options={
                                Object.entries(
                                    METRICS
                                ).map(
                                    ([
                                        value,
                                        item,
                                    ]) => ({
                                        value,

                                        label:
                                            item.unit
                                                ? `${t(
                                                    item.labelKey
                                                )} (${item.unit})`
                                                : t(
                                                    item.labelKey
                                                ),
                                    })
                                )
                            }
                        />
                    </div>

                    <div className="analysis-filter-item">
                        <label>
                            {t("analysis.conditions.target")}
                        </label>

                        {metricInfo.allowClosed ? (
                            <Select
                                value={
                                    includeClose
                                        ? "include"
                                        : "exclude"
                                }
                                style={{ width: 210 }}
                                onChange={(value) =>
                                    setIncludeClose(
                                        value === "include"
                                    )
                                }
                                options={[
                                    {
                                        label: t(
                                            "analysis.conditions.openAndClosed"
                                        ),
                                        value: "include",
                                    },
                                    {
                                        label: t(
                                            "analysis.conditions.openOnly"
                                        ),
                                        value: "exclude",
                                    },
                                ]}
                            />
                        ) : (
                            <Tooltip
                                title={t(
                                    "analysis.conditions.openOnlyHelp"
                                )}
                            >
                                <Button
                                    disabled
                                    style={{
                                        width: 210,
                                        textAlign: "left",
                                    }}
                                >
                                    {t(
                                        "analysis.conditions.openOnly"
                                    )}{" "}
                                    <InfoCircleOutlined />
                                </Button>
                            </Tooltip>
                        )}
                    </div>

                    <Button
                        type="primary"
                        size="large"
                        loading={
                            analysisLoading
                        }
                        onClick={
                            executeAnalysis
                        }
                    >
                        {t(
                            "analysis.run"
                        )}
                    </Button>
                </Space>

                {!metricInfo.allowClosed && (
                    <div className="analysis-metric-note">
                        <InfoCircleOutlined />

                        <span>
                            {t(
                                "analysis.conditions.openOnlyDescription"
                            )}
                        </span>
                    </div>
                )}
            </Card>

            {/* ===============================================
          RESULTS
      =============================================== */}

            {hasResult && (
                <div
                    ref={
                        reportRef
                    }
                    className="analysis-report"
                >

                    {/* RESULT HEADING */}

                    <div className="analysis-report-heading">
                        <div>
                            <h2>
                                {t(
                                    "analysis.results.comparison",
                                    {
                                        metric:
                                            metricLabel,
                                    }
                                )}
                            </h2>

                            <p>
                                {analyzedGroups
                                    .map(
                                        (group) =>
                                            group.name
                                    )
                                    .join(" / ")}
                            </p>
                        </div>

                        <Tag>
                            {metricInfo.allowClosed
                                ? effectiveIncludeClose
                                    ? t(
                                        "analysis.conditions.includeClose"
                                    )
                                    : t(
                                        "analysis.conditions.excludeClose"
                                    )
                                : t(
                                    "analysis.conditions.openOnly"
                                )}
                        </Tag>
                    </div>

                    {/* ===========================================
              SUMMARY CARDS
          =========================================== */}

                    <div className="analysis-multi-summary-grid">
                        {analyzedGroups.map(
                            (group) => {
                                const summary =
                                    summaries[
                                    group.id
                                    ];

                                if (!summary) {
                                    return null;
                                }

                                return (
                                    <Card
                                        key={
                                            group.id
                                        }
                                        className="analysis-multi-summary-card"
                                        style={{
                                            borderTop:
                                                `4px solid ${group.color}`,
                                        }}
                                    >
                                        <div className="summary-group-title">
                                            <span
                                                className="analysis-summary-color-dot"
                                                style={{
                                                    background:
                                                        group.color,
                                                }}
                                            />

                                            {
                                                group.name
                                            }
                                        </div>

                                        <div className="analysis-summary-primary">
                                            <div>
                                                <Statistic
                                                    title={
                                                        t(
                                                            "analysis.stats.detected"
                                                        )
                                                    }
                                                    value={
                                                        summary.detected
                                                    }
                                                />

                                                <div className="analysis-stat-caption">
                                                    Open{" "}
                                                    {
                                                        summary.openCount
                                                    }
                                                    {" / "}
                                                    Closed{" "}
                                                    {
                                                        summary.closedCount
                                                    }
                                                </div>
                                            </div>

                                            <Statistic
                                                title={
                                                    t(
                                                        "analysis.stats.openRate"
                                                    )
                                                }
                                                value={
                                                    summary.openRate
                                                }
                                                precision={1}
                                                suffix="%"
                                            />

                                            <Statistic
                                                title={
                                                    t(
                                                        "analysis.stats.analyzedN"
                                                    )
                                                }
                                                value={
                                                    summary.analyzedN
                                                }
                                            />
                                        </div>

                                        <Divider
                                            style={{
                                                margin:
                                                    "14px 0",
                                            }}
                                        />

                                        <div className="analysis-summary-secondary">
                                            <Statistic
                                                title={
                                                    t(
                                                        "analysis.stats.median"
                                                    )
                                                }
                                                value={
                                                    summary.median
                                                }
                                                precision={
                                                    metric ===
                                                        "axisRatio"
                                                        ? 3
                                                        : 2
                                                }
                                                suffix={
                                                    metricInfo.unit ||
                                                    undefined
                                                }
                                            />

                                            <Statistic
                                                title={
                                                    t(
                                                        "analysis.stats.mean"
                                                    )
                                                }
                                                value={
                                                    summary.mean
                                                }
                                                precision={
                                                    metric ===
                                                        "axisRatio"
                                                        ? 3
                                                        : 2
                                                }
                                                suffix={
                                                    metricInfo.unit ||
                                                    undefined
                                                }
                                            />
                                        </div>
                                    </Card>
                                );
                            }
                        )}
                    </div>

                    {/* ===========================================
              STATISTICAL ANALYSIS
          =========================================== */}

                    <Card className="analysis-test-card">
                        <div className="analysis-test-content">
                            <div>
                                <span className="analysis-small-label">
                                    {t(
                                        "analysis.test.heading"
                                    )}
                                </span>

                                <h3>
                                    {statisticalResult
                                        ?.test ||
                                        t(
                                            "analysis.test.title"
                                        )}
                                </h3>

                                <p>
                                    {analyzedGroups.length ===
                                        2
                                        ? t(
                                            "analysis.test.mannWhitneyDescription"
                                        )
                                        : t(
                                            "analysis.test.kruskalWallisDescription"
                                        )}
                                </p>
                            </div>

                            {statisticalResult ? (
                                <div className="analysis-test-result">
                                    <span>
                                        {t(
                                            "analysis.test.pValue"
                                        )}
                                    </span>

                                    <strong>
                                        {statisticalResult.p <
                                            0.0001
                                            ? "< 0.0001"
                                            : statisticalResult.p.toFixed(
                                                4
                                            )}
                                    </strong>

                                    {statisticalResult.p <
                                        0.05 ? (
                                        <Tag color="green">
                                            {t(
                                                "analysis.test.significant"
                                            )}
                                            {" *"}
                                        </Tag>
                                    ) : (
                                        <Tag>
                                            {t(
                                                "analysis.test.notSignificant"
                                            )}
                                        </Tag>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    {t(
                                        "analysis.test.notEnoughData"
                                    )}
                                </div>
                            )}
                        </div>

                        <Alert
                            className="analysis-statistics-note"
                            type="info"
                            showIcon
                            message={
                                t(
                                    "analysis.test.interpretationNote"
                                )
                            }
                        />
                    </Card>

                    {/* ===========================================
              BOX PLOT
          =========================================== */}

                    <Card
                        title={
                            `${t(
                                "analysis.charts.boxplot"
                            )}: ${metricDisplay}`
                        }
                        className="analysis-chart-card"
                    >
                        <MultiBoxPlot
                            groups={
                                analyzedGroups
                            }
                            metricUnit={
                                metricInfo.unit
                            }
                        />
                    </Card>

                    {/* ===========================================
              HISTOGRAM
          =========================================== */}

                    <Card
                        title={
                            `${t(
                                "analysis.charts.histogram"
                            )}: ${metricDisplay}`
                        }
                        className="analysis-chart-card"
                    >
                        <div className="analysis-histogram-help">
                            {t(
                                "analysis.charts.histogramHelp"
                            )}

                            <Tooltip
                                title={
                                    t(
                                        "analysis.charts.medianHelp"
                                    )
                                }
                            >
                                <InfoCircleOutlined
                                    style={{
                                        marginLeft:
                                            7,

                                        cursor:
                                            "help",
                                    }}
                                />
                            </Tooltip>
                        </div>

                        {metric === "aperture" &&
                            effectiveIncludeClose && (
                                <div className="analysis-metric-note">
                                    <InfoCircleOutlined />

                                    <span>
                                        {t(
                                            "analysis.conditions.closedZeroNote"
                                        )}
                                    </span>
                                </div>
                            )}

                        {!metricInfo.allowClosed && (
                            <div className="analysis-metric-note">
                                <InfoCircleOutlined />

                                <span>
                                    {t(
                                        "analysis.conditions.openOnlyDescription"
                                    )}
                                </span>
                            </div>
                        )}

                        <div className="analysis-histogram">
                            <ResponsiveContainer
                                width="100%"
                                height={390}
                            >
                                <BarChart
                                    data={
                                        histogram
                                    }
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                    />

                                    <XAxis
                                        dataKey="range"
                                        tick={{
                                            fontSize:
                                                11,
                                        }}
                                    />

                                    <YAxis />

                                    <RechartsTooltip />

                                    <Legend />

                                    {analyzedGroups.map(
                                        (group) => (
                                            <Bar
                                                key={
                                                    group.id
                                                }
                                                dataKey={
                                                    group.id
                                                }
                                                name={
                                                    group.name
                                                }
                                                fill={
                                                    group.color
                                                }
                                                opacity={
                                                    0.78
                                                }
                                                cursor="pointer"
                                                onClick={(data) =>
                                                    handleHistogramClick(
                                                        data,
                                                        group.id
                                                    )
                                                }
                                            />
                                        )
                                    )}
                                </BarChart>
                            </ResponsiveContainer>

                            <div className="analysis-axis-title">
                                {
                                    metricDisplay
                                }
                            </div>
                        </div>
                    </Card>

                    {/* ===========================================
              ACTUAL STOMATA
          =========================================== */}

                    {selectedHistogramBin && (
                        <Card
                            className="analysis-stoma-preview-card"
                            style={{
                                borderTop:
                                    `3px solid ${selectedHistogramBin.color}`,
                            }}
                            title={
                                <Space wrap>
                                    <span>
                                        {t(
                                            "analysis.preview.title"
                                        )}
                                    </span>

                                    <Tag
                                        color={
                                            selectedHistogramBin.color
                                        }
                                    >
                                        {
                                            selectedHistogramBin.groupName
                                        }
                                    </Tag>

                                    <Tag>
                                        {
                                            selectedHistogramBin.range
                                        }

                                        {metricInfo.unit
                                            ? ` ${metricInfo.unit}`
                                            : ""}
                                    </Tag>
                                </Space>
                            }
                            extra={
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setSelectedHistogramBin(
                                            null
                                        );

                                        setHistogramStomas(
                                            []
                                        );
                                    }}
                                >
                                    {t(
                                        "analysis.preview.close"
                                    )}
                                </Button>
                            }
                        >
                            {histogramPreviewLoading ? (
                                <div className="analysis-preview-loading">
                                    <Spin />

                                    <div>
                                        {t(
                                            "analysis.preview.loading"
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="histogram-stoma-summary">
                                        <strong>
                                            {
                                                histogramStomas.length
                                            }
                                        </strong>

                                        {" "}

                                        {t(
                                            "analysis.preview.matched"
                                        )}
                                    </div>

                                    {histogramStomas.length ===
                                        0 ? (
                                        <div className="analysis-empty">
                                            {t(
                                                "analysis.preview.empty"
                                            )}
                                        </div>
                                    ) : (
                                        <div className="histogram-stoma-grid">
                                            {histogramStomas.map(
                                                (item) => (
                                                    <div
                                                        key={`${item.photoKey}-${item.stomaIndex}`}
                                                        className="histogram-stoma-item"
                                                    >
                                                        <div className="histogram-stoma-image">
                                                            <img
                                                                src={
                                                                    item.image
                                                                }
                                                                alt={`stoma-${item.id}`}
                                                            />

                                                            <div className="stoma-number">
                                                                #
                                                                {
                                                                    item.id
                                                                }
                                                            </div>
                                                        </div>

                                                        <div className="histogram-stoma-data">
                                                            <div className="stoma-main-value">
                                                                {item.value.toFixed(
                                                                    metric ===
                                                                        "axisRatio"
                                                                        ? 3
                                                                        : 2
                                                                )}

                                                                {metricInfo.unit && (
                                                                    <span>
                                                                        {
                                                                            metricInfo.unit
                                                                        }
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <Tag
                                                                color={
                                                                    item.open
                                                                        ? "red"
                                                                        : "blue"
                                                                }
                                                            >
                                                                {item.label ||
                                                                    "-"}
                                                            </Tag>

                                                            {metric ===
                                                                "axisRatio" && (
                                                                    <div className="stoma-ratio-detail">
                                                                        Short{" "}
                                                                        {item.axisShort !==
                                                                            null
                                                                            ? item.axisShort.toFixed(
                                                                                2
                                                                            )
                                                                            : "-"}

                                                                        {" / "}

                                                                        Long{" "}
                                                                        {item.axisLong !==
                                                                            null
                                                                            ? item.axisLong.toFixed(
                                                                                2
                                                                            )
                                                                            : "-"}
                                                                    </div>
                                                                )}

                                                            <div className="stoma-source">
                                                                {
                                                                    item.sourceImage
                                                                }
                                                            </div>

                                                            {item.comment && (
                                                                <div className="stoma-comment">
                                                                    {
                                                                        item.comment
                                                                    }
                                                                </div>
                                                            )}

                                                            {(item.temperature ||
                                                                item.humidity) && (
                                                                    <div className="stoma-environment">
                                                                        {item.temperature &&
                                                                            `${t(
                                                                                "analysis.preview.temperature"
                                                                            )} ${item.temperature}℃`}

                                                                        {item.temperature &&
                                                                            item.humidity &&
                                                                            " / "}

                                                                        {item.humidity &&
                                                                            `${t(
                                                                                "analysis.preview.humidity"
                                                                            )} ${item.humidity}%`}
                                                                    </div>
                                                                )}
                                                        </div>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </Card>
                    )}

                    {/* ===========================================
              SUMMARY DETAILS
          =========================================== */}

                    <Card
                        title={
                            t(
                                "analysis.detail"
                            )
                        }
                        className="analysis-detail-card"
                    >
                        <div className="analysis-result-table-wrap">
                            <table className="analysis-result-table">
                                <thead>
                                    <tr>
                                        <th>
                                            {t(
                                                "analysis.table.group"
                                            )}
                                        </th>

                                        <th>
                                            {t(
                                                "analysis.stats.imageCount"
                                            )}
                                        </th>

                                        <th>
                                            {t(
                                                "analysis.stats.detected"
                                            )}
                                        </th>

                                        <th>
                                            {t(
                                                "analysis.stats.openRate"
                                            )}
                                        </th>

                                        <th>
                                            {t(
                                                "analysis.stats.analyzedN"
                                            )}
                                        </th>

                                        <th>
                                            {t(
                                                "analysis.stats.median"
                                            )}
                                        </th>

                                        <th>
                                            {t(
                                                "analysis.stats.mean"
                                            )}
                                        </th>

                                        <th>
                                            {t(
                                                "analysis.stats.sd"
                                            )}
                                        </th>

                                        <th>
                                            Q1
                                        </th>

                                        <th>
                                            Q3
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {analyzedGroups.map(
                                        (group) => {
                                            const summary =
                                                summaries[
                                                group.id
                                                ];

                                            const precision =
                                                metric ===
                                                    "axisRatio"
                                                    ? 3
                                                    : 2;

                                            return (
                                                <tr
                                                    key={
                                                        group.id
                                                    }
                                                >
                                                    <td>
                                                        <Tag
                                                            color={
                                                                group.color
                                                            }
                                                        >
                                                            {
                                                                group.name
                                                            }
                                                        </Tag>
                                                    </td>

                                                    <td>
                                                        {
                                                            group.docs.length
                                                        }
                                                    </td>

                                                    <td>
                                                        {
                                                            summary.detected
                                                        }
                                                    </td>

                                                    <td>
                                                        {summary.openRate.toFixed(
                                                            1
                                                        )}
                                                        %
                                                    </td>

                                                    <td>
                                                        {
                                                            summary.analyzedN
                                                        }
                                                    </td>

                                                    <td>
                                                        {summary.median.toFixed(
                                                            precision
                                                        )}
                                                    </td>

                                                    <td>
                                                        {summary.mean.toFixed(
                                                            precision
                                                        )}
                                                    </td>

                                                    <td>
                                                        {summary.sd.toFixed(
                                                            precision
                                                        )}
                                                    </td>

                                                    <td>
                                                        {summary.q1.toFixed(
                                                            precision
                                                        )}
                                                    </td>

                                                    <td>
                                                        {summary.q3.toFixed(
                                                            precision
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Card>

                    {/* ===========================================
              RESEARCH BACKGROUND
          =========================================== */}

                    <Card className="analysis-research-card">
                        <div className="analysis-research-content">
                            <div className="analysis-research-icon">
                                <ExperimentOutlined />
                            </div>

                            <div>
                                <div className="analysis-research-label">
                                    {t(
                                        "analysis.research.heading"
                                    )}
                                </div>

                                <h3>
                                    {t(
                                        "analysis.research.title"
                                    )}
                                </h3>

                                <p>
                                    {t(
                                        "analysis.research.description"
                                    )}
                                </p>

                                <div className="analysis-publication">
                                    <strong>
                                        Takagi M, Hirata R, Aihara Y, et al. (2023)
                                    </strong>

                                    <div>
                                        <em>
                                            Image-Based Quantification of Arabidopsis thaliana Stomatal Aperture from Leaf Images
                                        </em>
                                    </div>

                                    <div>
                                        Plant & Cell Physiology, 64(11), 1301–1310.
                                    </div>

                                    <a
                                        href="https://doi.org/10.1093/pcp/pcad018"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {t(
                                            "analysis.research.viewPublication"
                                        )}
                                        {" ↗"}
                                    </a>
                                </div>

                                <p className="analysis-research-current">
                                    {t(
                                        "analysis.research.currentModel"
                                    )}
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}

export default Analysis;